import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';

const cache = new Map();
const CACHE_TTL = 1000 * 60 * 50; // 50 minutes (URLs valid for 1hr)
const EXPIRY_SECONDS = 60 * 60;

function cached(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path; // legacy: already a full URL
  const hit = cache.get(path);
  if (hit && Date.now() - hit.ts < CACHE_TTL) return hit.url;
  return null;
}

/**
 * Resolve a list of storage paths to signed URLs in a single request.
 * Anything already cached (or a legacy full URL) is returned without a round-trip.
 */
async function resolveMany(paths) {
  const out = paths.map(p => cached(p));
  const need = [];
  paths.forEach((p, i) => {
    if (p && !out[i]) need.push({ path: p, i });
  });
  if (need.length === 0) return out;

  const { data, error } = await supabase.storage
    .from('evidence-photos')
    .createSignedUrls(need.map(n => n.path), EXPIRY_SECONDS);

  if (error || !data) return out;

  data.forEach((row, k) => {
    const { path, i } = need[k];
    if (row?.signedUrl) {
      cache.set(path, { url: row.signedUrl, ts: Date.now() });
      out[i] = row.signedUrl;
    }
  });
  return out;
}

/**
 * Resolves a storage path to a signed URL on-demand.
 * Returns null while loading, then the signed URL.
 * If the value is already a full URL (legacy data), returns it as-is.
 */
export function usePhotoUrl(path) {
  const [url, setUrl] = useState(() => cached(path));

  useEffect(() => {
    if (!path) { setUrl(null); return; }
    const hit = cached(path);
    if (hit) { setUrl(hit); return; }

    let cancelled = false;
    (async () => {
      const [resolved] = await resolveMany([path]);
      if (!cancelled && resolved) setUrl(resolved);
    })();

    return () => { cancelled = true; };
  }, [path]);

  return url;
}

/**
 * Resolve multiple photo paths at once (convenience for evidence cards).
 */
export function usePhotoUrls(paths) {
  const fault = usePhotoUrl(paths?.fault || paths?.photo_fault_url);
  const repair = usePhotoUrl(paths?.repair || paths?.photo_repair_url);
  const completed = usePhotoUrl(paths?.completed || paths?.photo_completed_url);
  const additional = usePhotoUrl(paths?.additional || paths?.photo_additional_url);
  return { fault, repair, completed, additional };
}

/**
 * Resolve an array of photo paths to signed URLs.
 * Handles any number of photos — entries routinely carry 15–30.
 * Returns an array the same length as `paths`; entries are null until resolved.
 */
export function usePhotoUrlList(paths) {
  // Stable dependency so the effect doesn't refire on every render
  const key = JSON.stringify(paths || []);
  const list = useMemo(() => JSON.parse(key), [key]);
  const [urls, setUrls] = useState(() => list.map(p => cached(p)));

  useEffect(() => {
    if (list.length === 0) { setUrls([]); return; }
    setUrls(list.map(p => cached(p)));

    let cancelled = false;
    (async () => {
      const resolved = await resolveMany(list);
      if (!cancelled) setUrls(resolved);
    })();

    return () => { cancelled = true; };
  }, [key]);

  return urls;
}
